import { useEffect, useState } from "react";
import { COURSE_TYPE_CONFIG } from "../../config/COURSE_TYPE_CONFIG";
import { DeclarationStatusEnumColor, UrssafServiceStatEnumColor } from "../../Enum/UrssafStatusEnum";
import PunctualCourseRepository from "../../Repositories/PunctualCourseRepository";
import { isVerifiedTrainer } from "../../Utils/VerifiedTrainerAmounts";
import { courseAmount } from "./courseRows";
import { courseStatusLabel } from "./courseStatus";
import { isMonthPeriod, periodBounds } from "./tabs";

export interface StatusTotal {
    count: number;
    amount: number;
    /** Faux quand la période compte plus de lignes que la page chargée : le montant est alors un minimum. */
    complete: boolean;
    /** Dont cours en revue chez l'équipe (attestation, approbation) ou justificatif à renvoyer. */
    notDeclared?: number;
    /** Dont cours terminés pas encore transmis à l'URSSAF, sans statut de déplacement. */
    notTransmitted?: number;
}

export interface PastPeriodStats {
    /** Reçu sur la période (statuts Validé, Collecté, Prélevée) : somme des montants du tableau (prix total TTC chez Verified Trainer, rémunération ailleurs), ou l'API des montants sur toutes les périodes. */
    received: number | null;
    halfHours: number | null;
    clientCount: number | null;
    /** Nombre de cours passés de la période, sans filtre. */
    total: number | null;
    /** Transmis ou en attente de validation par le client, ou en revue chez l'équipe (attestation, approbation). */
    pending: StatusTotal | null;
    /** Refusés, impayés, annulés, ou justificatif de déplacement à renvoyer. */
    refused: StatusTotal | null;
    loading: boolean;
}

const EMPTY: PastPeriodStats = { received: null, halfHours: null, clientCount: null, total: null, pending: null, refused: null, loading: false };
const S = UrssafServiceStatEnumColor;
const D = DeclarationStatusEnumColor;
// Classement par le libellé de statut affiché dans le tableau (même résolution que la pastille).
// Les cours pas encore transmis à l'URSSAF (règles de distance) comptent aussi :
// terminés pas encore transmis, attestation à vérifier et attente d'approbation en attente,
// justificatif à renvoyer en refusés.
const RECEIVED_LABELS = [S.STATUS_30_VALIDATED.title, S.STATUS_50_COLLECTED.title, S.STATUS_70_PAID.title];
const PENDING_LABELS = [S.STATUS_10_INTEGRATED.title, S.STATUS_20_WAITING.title, S.EN_COURS.title, D.PENDING_VERIFICATION.title, D.PENDING_APPROVAL.title];
const REFUSED_LABELS = [S.STATUS_40_REFUSAL.title, S.STATUS_60_REFUSAL_OF_DIRECT_DEBIT.title, S.STATUS_110_CANCELED.title, S.STATUS_111_CANCELED_111.title, S.STATUS_112_CANCELED_112.title, D.REJECTED.title];
const ROWS_LIMIT = 200;
const PAST_CONFIG = COURSE_TYPE_CONFIG.historique;

const NOT_DECLARED_LABELS = [D.PENDING_VERIFICATION.title, D.PENDING_APPROVAL.title, D.REJECTED.title];

const withLabel = (rows: any[], labels: string[]): any[] => rows.filter((c) => labels.includes(courseStatusLabel(c, PAST_CONFIG)));

const sumByStatus = (rows: any[], labels: string[], complete: boolean): StatusTotal => {
    const matching = withLabel(rows, labels);
    return {
        count: matching.length,
        amount: matching.reduce((n, c) => n + courseAmount(c), 0),
        complete,
        notDeclared: withLabel(matching, NOT_DECLARED_LABELS).length,
        notTransmitted: withLabel(matching, [S.EN_COURS.title]).length,
    };
};

/**
 * Chiffres de l'onglet Passés pour la période choisie. Sur un mois, les trois
 * totaux (reçu, en attente, refusés) sont calculés sur les cours passés du
 * mois, chargés en une page large : ce sont les montants « Vous recevez » du
 * tableau. Sur « toutes les périodes », le reçu vient de l'API des montants
 * (celle du bouton « Total facturé » actuel), qui compte aussi les cours
 * validés non encore passés.
 */
export default function usePastPeriodStats(teachrId: number | null | undefined, period: string | null): PastPeriodStats {
    const [stats, setStats] = useState<PastPeriodStats>(EMPTY);

    useEffect(() => {
        if (!teachrId || !period) {
            setStats(EMPTY);
            return;
        }
        let cancelled = false;
        const { start, end } = periodBounds(period);
        const month = isMonthPeriod(period);
        setStats((prev) => ({ ...prev, loading: true }));
        Promise.allSettled([
            month ? Promise.resolve(null) : PunctualCourseRepository.getMontantFacture(teachrId, start, end),
            PunctualCourseRepository.searchPunctualCoursePastByTeachrNew(0, teachrId, "", "", start, end, month ? ROWS_LIMIT : 1),
        ]).then(([amount, rows]) => {
            if (cancelled) return;
            const next: PastPeriodStats = { ...EMPTY, loading: false };
            if (!month && amount.status === "fulfilled" && amount.value) {
                const res = amount.value;
                // `amount` est le montant facturé TTC : c'est lui qu'on met en avant chez Verified Trainer, comme la liste
                // actuelle. Ailleurs « reçu » est la rémunération enregistrée (`remuneration`, part HT + TVA des charges),
                // la seule juste pour un prof Teach'r passé par la coopérative ; repli sur `amount`.
                const received = Number((isVerifiedTrainer() ? res?.amount : res?.remuneration ?? res?.amount) ?? 0);
                next.received = isNaN(received) ? 0 : received;
                next.halfHours = res?.duration != null ? Number(res.duration) : null;
                next.clientCount = res?.clientCount != null ? Number(res.clientCount) : null;
            }
            if (rows.status === "fulfilled" && rows.value) {
                const list: any[] = rows.value.courses ?? [];
                const total: number = rows.value.totalItems ?? list.length;
                next.total = total;
                if (month) {
                    const complete = list.length >= total;
                    const received = withLabel(list, RECEIVED_LABELS);
                    next.received = received.reduce((n, c) => n + courseAmount(c), 0);
                    next.halfHours = received.reduce((n, c) => n + (Number(c?.duration ?? 0) || 0), 0);
                    next.clientCount = new Set(received.map((c) => c?.forChild_id ?? c?.clientId ?? c?.id)).size;
                    next.pending = sumByStatus(list, PENDING_LABELS, complete);
                    next.refused = sumByStatus(list, REFUSED_LABELS, complete);
                }
            }
            setStats(next);
        });
        return () => { cancelled = true; };
    }, [teachrId, period]);

    return stats;
}
